import { create } from 'zustand';
import { FileNode, Project, EditorTab, ConsoleMessage } from '../types';

interface EditorStore {
  project: Project;
  tabs: EditorTab[];
  activeFileId: string;
  consoleMessages: ConsoleMessage[];
  isPreviewLoading: boolean;
  externalLibraries: string[];
  previewKey: number;

  // Actions
  setActiveFile: (fileId: string) => void;
  updateFileContent: (fileId: string, content: string) => void;
  createFile: (name: string, language: FileNode['language'], parentId?: string) => string;
  deleteFile: (fileId: string) => void;
  renameFile: (fileId: string, name: string) => void;
  addTab: (fileId: string) => void;
  removeTab: (fileId: string) => void;
  addConsoleMessage: (message: Omit<ConsoleMessage, 'id' | 'timestamp'>) => void;
  clearConsole: () => void;
  setPreviewLoading: (loading: boolean) => void;
  addExternalLibrary: (url: string) => void;
  removeExternalLibrary: (url: string) => void;
  incrementPreviewKey: () => void;
  loadProject: (project: Project) => void;
}

const createInitialProject = (): Project => {
  const htmlFileId = 'index.html';
  const cssFileId = 'styles.css';
  const jsFileId = 'script.js';

  return {
    id: 'default',
    name: '我的项目',
    files: {
      [htmlFileId]: {
        id: htmlFileId,
        name: 'index.html',
        type: 'file',
        language: 'html',
        content: `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>我的项目</title>
</head>
<body>
  <div id="app">
    <h1>欢迎使用在线代码编辑器！</h1>
    <p>开始编写你的代码吧</p>
  </div>
</body>
</html>`
      },
      [cssFileId]: {
        id: cssFileId,
        name: 'styles.css',
        type: 'file',
        language: 'css',
        content: `body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  margin: 0;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  min-height: 100vh;
}

h1 {
  font-size: 2.5em;
  margin-bottom: 10px;
}

#app {
  background: rgba(255, 255, 255, 0.1);
  padding: 30px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
}`
      },
      [jsFileId]: {
        id: jsFileId,
        name: 'script.js',
        type: 'file',
        language: 'javascript',
        content: `console.log('Hello, World!');
console.log('欢迎使用在线代码编辑器！');

// 你可以在这里编写JavaScript代码
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM已加载完成');
});`
      }
    },
    rootFileId: htmlFileId,
    openTabs: [htmlFileId, cssFileId, jsFileId],
    activeFileId: htmlFileId,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
};

export const useEditorStore = create<EditorStore>((set) => ({
  project: createInitialProject(),
  tabs: [
    { fileId: 'index.html', name: 'index.html', language: 'html' },
    { fileId: 'styles.css', name: 'styles.css', language: 'css' },
    { fileId: 'script.js', name: 'script.js', language: 'javascript' }
  ],
  activeFileId: 'index.html',
  consoleMessages: [],
  isPreviewLoading: false,
  externalLibraries: [],
  previewKey: 0,

  setActiveFile: (fileId) => set({ activeFileId: fileId }),

  updateFileContent: (fileId, content) => set((state) => {
    const updatedFiles = {
      ...state.project.files,
      [fileId]: {
        ...state.project.files[fileId],
        content,
        updatedAt: Date.now()
      }
    };
    return {
      project: { ...state.project, files: updatedFiles },
      previewKey: state.previewKey + 1
    };
  }),

  createFile: (name, language, parentId) => {
    const id = Date.now().toString();
    const newFile: FileNode = {
      id,
      name,
      type: 'file',
      language,
      content: '',
      parentId
    };
    set((state) => ({
      project: {
        ...state.project,
        files: { ...state.project.files, [id]: newFile }
      }
    }));
    return id;
  },

  deleteFile: (fileId) => set((state) => {
    const { [fileId]: deleted, ...remainingFiles } = state.project.files;
    const updatedTabs = state.tabs.filter(tab => tab.fileId !== fileId);
    let newActiveFileId = state.activeFileId;
    if (state.activeFileId === fileId && updatedTabs.length > 0) {
      newActiveFileId = updatedTabs[0].fileId;
    }
    return {
      project: { ...state.project, files: remainingFiles },
      tabs: updatedTabs,
      activeFileId: newActiveFileId
    };
  }),

  renameFile: (fileId, name) => set((state) => ({
    project: {
      ...state.project,
      files: {
        ...state.project.files,
        [fileId]: { ...state.project.files[fileId], name }
      }
    }
  })),

  addTab: (fileId) => set((state) => {
    const file = state.project.files[fileId];
    if (!file || state.tabs.some(tab => tab.fileId === fileId)) return state;

    return {
      tabs: [...state.tabs, { fileId, name: file.name, language: file.language }],
      activeFileId: fileId
    };
  }),

  removeTab: (fileId) => set((state) => {
    const updatedTabs = state.tabs.filter(tab => tab.fileId !== fileId);
    let newActiveFileId = state.activeFileId;
    if (state.activeFileId === fileId && updatedTabs.length > 0) {
      newActiveFileId = updatedTabs[0].fileId;
    }
    return { tabs: updatedTabs, activeFileId: newActiveFileId };
  }),

  addConsoleMessage: (message) => set((state) => ({
    consoleMessages: [
      ...state.consoleMessages,
      {
        ...message,
        id: Date.now().toString(),
        timestamp: Date.now()
      }
    ]
  })),

  clearConsole: () => set({ consoleMessages: [] }),

  setPreviewLoading: (loading) => set({ isPreviewLoading: loading }),

  addExternalLibrary: (url) => set((state) => ({
    externalLibraries: [...state.externalLibraries, url]
  })),

  removeExternalLibrary: (url) => set((state) => ({
    externalLibraries: state.externalLibraries.filter(lib => lib !== url)
  })),

  incrementPreviewKey: () => set((state) => ({ previewKey: state.previewKey + 1 })),

  loadProject: (project) => set({
    project,
    tabs: project.openTabs.map(fileId => {
      const file = project.files[fileId];
      return { fileId, name: file.name, language: file.language };
    }),
    activeFileId: project.activeFileId
  })
}));
